import { useState } from "react";
import { router } from "@inertiajs/react";
import { toast } from "sonner";
import TemplateTagDialog from "../../components/TemplateTagDialog";
import EmailContentEditor from "../../components/EmailContentEditor";
import { isHtmlEmpty } from "../../lib/htmlToText";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu-portal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Upload,
  Plus,
  Folder,
  FileText,
  ChevronDown,
  ChevronRight,
  Download,
  MoreVertical,
  Edit,
  Trash2,
  FolderOpen,
  Tag,
  Copy,
  X,
  Search,
} from "lucide-react";

interface Tag {
  id: number;
  name: string;
  color: string;
}

interface Template {
  id: number;
  name: string;
  html_content: string | null;
  text_content: string | null;
  folder_id: number;
  folder_name: string;
  uploaded_by: string;
  tag_ids?: number[];
  tags?: Tag[];
  created_at: string;
}

interface TemplateFolder {
  id: number;
  name: string;
  description: string;
  templates_count: number;
}

interface CreativesProps {
  offer: {
    id: number;
    name: string;
  };
  templates: Template[];
  template_folders: TemplateFolder[];
  tags: Tag[];
  is_admin: boolean;
  onPreviewTemplate: (template: Template) => void;
  onEditTemplate: (template: Template) => void;
}

export default function Creatives({ 
  offer, 
  templates, 
  template_folders, 
  tags,
  is_admin, 
  onPreviewTemplate,
  onEditTemplate 
}: CreativesProps) {
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null);
  const [templateSearch, setTemplateSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadHtmlContent, setUploadHtmlContent] = useState("");
  // File awaiting a replace confirmation (existing content would be overwritten)
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadTextContent, setUploadTextContent] = useState("");
  const [uploadName, setUploadName] = useState("");
  const [uploadFolder, setUploadFolder] = useState<number | null>(null);
  const [uploadAccordionOpen, setUploadAccordionOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<TemplateFolder | null>(null);
  const [folderError, setFolderError] = useState<string>("");
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [templateToMove, setTemplateToMove] = useState<Template | null>(null);
  const [targetFolder, setTargetFolder] = useState<number | null>(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [templateToCopy, setTemplateToCopy] = useState<Template | null>(null);
  const [copyTargetFolder, setCopyTargetFolder] = useState<number | null>(null);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [templateForTag, setTemplateForTag] = useState<Template | null>(null);
  const [editNameDialogOpen, setEditNameDialogOpen] = useState(false);
  const [templateToRename, setTemplateToRename] = useState<Template | null>(null);
  const [newTemplateName, setNewTemplateName] = useState("");

  const handleDownloadTemplate = (template: Template) => {
    // Text-only templates have no html_content -- fall back to the text part
    const contents = template.html_content || template.text_content || "";
    const blob = new Blob([contents], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${template.name}.html`, {
      duration: 3000,
      description: "Template saved to your downloads folder",
    });
  };

  const inFolder =
    selectedFolder === null
      ? templates
      : templates.filter((t) => t.folder_id === selectedFolder);

  const search = templateSearch.trim().toLowerCase();
  const filteredTemplates = search
    ? inFolder.filter((t) => t.name.toLowerCase().includes(search))
    : inFolder;

  const derivedName = (file: File) => file.name.replace(/\.html?$/i, "");

  // Read the chosen file into the editor so it shows up in Design and Preview
  // immediately, instead of being an opaque attachment
  const loadHtmlFile = (file: File) => {
    // Keep the name in step with the file unless it's been typed over by hand
    const derived = derivedName(file);
    setUploadName((current) =>
      !current.trim() || (uploadFile && current === derivedName(uploadFile))
        ? derived
        : current
    );

    const reader = new FileReader();
    reader.onload = () => {
      setUploadHtmlContent(String(reader.result ?? ""));
      setUploadFile(file);
    };
    reader.onerror = () =>
      toast.error("Could not read that file", {
        duration: 5000,
        description: "Try again, or paste the HTML into the editor",
      });
    reader.readAsText(file);
  };

  const handleHtmlFileChange = (
    file: File | null,
    input: HTMLInputElement
  ) => {
    // Reset so re-picking the same file still fires onChange
    input.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".html")) {
      toast.error("That isn't an HTML file", {
        duration: 5000,
        description: "Choose a .html file for the HTML part",
      });
      return;
    }

    // Replacing existing content -> confirm via a dialog rather than load over it
    if (!isHtmlEmpty(uploadHtmlContent)) {
      setPendingFile(file);
      return;
    }

    loadHtmlFile(file);
  };

  // Clearing the content in the editor drops the file it came from too, so the
  // form doesn't keep claiming a file whose markup is no longer there
  const handleUploadHtmlChange = (next: string) => {
    setUploadHtmlContent(next);
    if (isHtmlEmpty(next)) setUploadFile(null);
  };

  const handleConfirmReplace = () => {
    if (pendingFile) loadHtmlFile(pendingFile);
    setPendingFile(null);
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadFolder(null);
    setUploadHtmlContent("");
    setUploadTextContent("");
    setUploadName("");
  };

  // Either part is enough on its own, but a template can't be empty
  const hasUploadContent =
    !isHtmlEmpty(uploadHtmlContent) || uploadTextContent.trim().length > 0;
  const canUpload = hasUploadContent && !!uploadFolder && !!uploadName.trim();

  const handleFileUpload = async () => {
    if (!hasUploadContent) {
      toast.error("Add an HTML file or text content");
      return;
    }

    if (!uploadName.trim()) {
      toast.error("Give the template a name");
      return;
    }

    if (!uploadFolder) {
      toast.error("Select a folder for this template");
      return;
    }

    const formData = new FormData();
    formData.append("name", uploadName.trim());
    formData.append("folder_id", uploadFolder.toString());
    // The editor holds the markup now -- whether it was typed, pasted or
    // loaded from a file -- so send it directly rather than the file itself
    formData.append(
      "html_content",
      isHtmlEmpty(uploadHtmlContent) ? "" : uploadHtmlContent
    );
    formData.append("text_content", uploadTextContent.trim());

    router.post(`/offers/${offer.id}/templates`, formData as any, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        resetUploadForm();
        setUploadAccordionOpen(false);
        toast.success("Template uploaded successfully!", {
          duration: 3000,
          description: `${uploadName.trim()} has been added to the offer`,
        });
      },
      onError: (errors) => {
        console.error("Upload failed:", errors);
        toast.error("Failed to upload template", {
          duration: 5000,
          description: "Please check the file format and try again",
        });
      },
    });
  };

  const handleCreateFolder = () => {
    setFolderError(""); // Clear previous errors
    
    if (editingFolder) {
      router.put(
        `/offers/${offer.id}/template_folders/${editingFolder.id}`,
        {
          name: newFolderName,
        },
        {
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => {
            setDialogOpen(false);
            setNewFolderName("");
            setEditingFolder(null);
            setFolderError("");
            toast.success("Folder updated successfully!", {
              duration: 3000,
              description: `${newFolderName} has been updated`,
            });
          },
          onError: (errors) => {
            // Keep dialog open and show error
            if (errors?.name) {
              setFolderError(errors.name);
            } else {
              setFolderError("Name has already been taken");
            }
          },
        }
      );
    } else {
      router.post(
        `/offers/${offer.id}/template_folders`,
        {
          name: newFolderName,
        },
        {
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => {
            setDialogOpen(false);
            setNewFolderName("");
            setFolderError("");
            toast.success("Folder created successfully!", {
              duration: 3000,
              description: `${newFolderName} has been added`,
            });
          },
          onError: (errors) => {
            // Keep dialog open and show error
            if (errors?.name) {
              setFolderError(errors.name);
            } else {
              setFolderError("Name has already been taken");
            }
          },
        }
      );
    }
  };

  const handleEditFolder = (folder: TemplateFolder) => {
    setEditingFolder(folder);
    setNewFolderName(folder.name);
    setDialogOpen(true);
  };

  const handleDeleteFolder = (folder: TemplateFolder) => {
    if (confirm(`Are you sure you want to delete the folder "${folder.name}"? This action cannot be undone.`)) {
      router.delete(`/offers/${offer.id}/template_folders/${folder.id}`, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          if (selectedFolder === folder.id) {
            setSelectedFolder(null);
          }
          toast.success("Folder deleted successfully!", {
            duration: 3000,
            description: `${folder.name} has been deleted`,
          });
        },
        onError: () => {
          toast.error("Failed to delete folder", {
            duration: 5000,
            description: "Please try again",
          });
        },
      });
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewFolderName("");
    setEditingFolder(null);
    setFolderError("");
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      handleDialogClose();
    } else {
      setDialogOpen(true);
    }
  };

  const handleMoveToFolder = (template: Template) => {
    setTemplateToMove(template);
    setTargetFolder(template.folder_id);
    setMoveDialogOpen(true);
  };

  const handleConfirmMove = () => {
    if (!templateToMove || targetFolder === null) return;

    router.put(
      `/offers/${offer.id}/templates/${templateToMove.id}/move`,
      { folder_id: targetFolder },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          setMoveDialogOpen(false);
          setTemplateToMove(null);
          setTargetFolder(null);
          toast.success("Template moved successfully!", {
            duration: 3000,
            description: `${templateToMove.name} has been moved`,
          });
        },
        onError: () => {
          toast.error("Failed to move template", {
            duration: 5000,
            description: "Please try again",
          });
        },
      }
    );
  };

  const handleCopyToFolder = (template: Template) => {
    setTemplateToCopy(template);
    setCopyTargetFolder(template.folder_id);
    setCopyDialogOpen(true);
  };

  const handleConfirmCopy = () => {
    if (!templateToCopy || copyTargetFolder === null) return;

    router.post(
      `/offers/${offer.id}/templates/${templateToCopy.id}/copy`,
      { folder_id: copyTargetFolder },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          setCopyDialogOpen(false);
          setTemplateToCopy(null);
          setCopyTargetFolder(null);
          toast.success("Template copied successfully!", {
            duration: 3000,
            description: `${templateToCopy.name} has been copied`,
          });
        },
        onError: (errors) => {
          const errorMessage = errors?.errors?.[0] || "Failed to copy template";
          toast.error(errorMessage, {
            duration: 5000,
            description: errorMessage.includes("same folder") ? "Please select a different folder" : "Please try again",
          });
        },
      }
    );
  };

  const handleManageTag = (template: Template) => {
    setTemplateForTag(template);
    setTagDialogOpen(true);
  };

  const handleEditName = (template: Template) => {
    setTemplateToRename(template);
    setNewTemplateName(template.name);
    setEditNameDialogOpen(true);
  };

  const handleConfirmRename = () => {
    if (!templateToRename || !newTemplateName.trim()) return;

    router.put(
      `/offers/${offer.id}/templates/${templateToRename.id}`,
      { name: newTemplateName.trim() },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          setEditNameDialogOpen(false);
          setTemplateToRename(null);
          setNewTemplateName("");
          toast.success("Template renamed successfully!", {
            duration: 3000,
            description: `Template has been renamed to ${newTemplateName}.html`,
          });
        },
        onError: () => {
          toast.error("Failed to rename template", {
            duration: 5000,
            description: "Please try again",
          });
        },
      }
    );
  };

  const getColorClass = (color: string) => {
    switch (color) {
      case "green":
        return "bg-green-100 text-green-800 border-green-200";
      case "yellow":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "red":
        return "bg-red-100 text-red-800 border-red-200";
      case "blue":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "purple":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "gray":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getCardBorderClass = (color: string) => {
    switch (color) {
      case "green":
        return "border-green-500";
      case "yellow":
        return "border-yellow-500";
      case "red":
        return "border-red-500";
      case "blue":
        return "border-blue-500";
      case "purple":
        return "border-purple-500";
      case "gray":
        return "border-gray-400";
      default:
        return "border-gray-200";
    }
  };

  const handleDiscardTemplate = (template: Template) => {
    if (confirm(`Are you sure you want to delete "${template.name}"? This action cannot be undone.`)) {
      router.delete(`/offers/${offer.id}/templates/${template.id}`, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          toast.success("Template deleted successfully!", {
            duration: 3000,
            description: `${template.name} has been deleted`,
          });
        },
        onError: () => {
          toast.error("Failed to delete template", {
            duration: 5000,
            description: "Please try again",
          });
        },
      });
    }
  };

  return (
    <div className="space-y-6">
      {is_admin && (
        <div className="bg-white rounded-xl border border-gray-200">
          <button
            onClick={() => setUploadAccordionOpen(!uploadAccordionOpen)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900">
                Upload New Template
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Add HTML templates to your offer
              </p>
            </div>
            {uploadAccordionOpen ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {uploadAccordionOpen && (
            <div className="border-t border-gray-200">
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label
                      htmlFor="html-file"
                      className="text-sm font-medium text-gray-700 mb-2"
                    >
                      HTML File
                    </Label>
                    <label
                      htmlFor="html-file"
                      className="flex items-center justify-center w-full h-10 px-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                    >
                      <Upload className="w-4 h-4 mr-1.5" />
                      {uploadFile ? uploadFile.name : "Choose File"}
                    </label>
                    <input
                      id="html-file"
                      type="file"
                      accept=".html"
                      onChange={(e) =>
                        handleHtmlFileChange(e.target.files?.[0] || null, e.target)
                      }
                      className="hidden"
                    />
                    {uploadFile && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
                        <span className="truncate">{uploadFile.name}</span>
                        <button
                          type="button"
                          onClick={() => setUploadFile(null)}
                          className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer"
                          title="Remove HTML file"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label
                      htmlFor="template-name-input"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Template Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="template-name-input"
                      value={uploadName}
                      onChange={(e) => setUploadName(e.target.value)}
                      placeholder={
                        uploadFile ? "From the file name" : "Name this template"
                      }
                      className="h-10"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="folder-select"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Folder <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="folder-select"
                      value={uploadFolder || ""}
                      onChange={(e) =>
                        setUploadFolder(Number(e.target.value))
                      }
                      className="block w-full h-10 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm cursor-pointer focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select a folder</option>
                      {template_folders.map((folder) => (
                        <option key={folder.id} value={folder.id}>
                          {folder.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2">
                    Content
                  </Label>
                  <EmailContentEditor
                    html={uploadHtmlContent}
                    onHtmlChange={handleUploadHtmlChange}
                    text={uploadTextContent}
                    onTextChange={setUploadTextContent}
                  />
                  <p className="mt-1.5 text-xs text-gray-500">
                    Paste content into Design or raw HTML into HTML, choose a file
                    above, or edit the markup in HTML. Preview shows the rendered
                    result.
                  </p>
                </div>

                <div className="flex items-center justify-end gap-3">
                  <Button
                    onClick={handleFileUpload}
                    className="bg-blue-600 hover:bg-blue-700 cursor-pointer"
                    disabled={!canUpload}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload Template
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="grid grid-cols-12 h-[700px] overflow-hidden rounded-xl">
          {/* Left Pane - Folders */}
          <div className="col-span-3 bg-gray-50 border-r border-gray-200">
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Folders
                </h3>
                {is_admin && (
                  <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="cursor-pointer">
                        <Plus className="w-4 h-4 mr-1" />
                        Create Folder
                      </Button>
                    </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingFolder ? "Edit Folder" : "Create New Folder"}</DialogTitle>
                      <DialogDescription>
                        {editingFolder ? "Edit the folder details." : "Create a new folder to organize your templates."}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="folder-name">Folder Name</Label>
                        <Input
                          id="folder-name"
                          value={newFolderName}
                          onChange={(e) => {
                            setNewFolderName(e.target.value);
                            setFolderError(""); // Clear error when typing
                          }}
                          placeholder="Enter folder name"
                          className={folderError ? "border-red-500" : ""}
                        />
                        {folderError && (
                          <p className="text-sm text-red-600 mt-1">{folderError}</p>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={handleDialogClose}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateFolder}
                        className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer"
                      >
                        {editingFolder ? "Update Folder" : "Create Folder"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>

            <div
              className="p-3 overflow-y-auto"
              style={{ height: "calc(100% - 60px)" }}
            >
              <nav className="space-y-1">
                <button
                  onClick={() => setSelectedFolder(null)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-all cursor-pointer ${
                    selectedFolder === null
                      ? "bg-blue-100 text-blue-700 font-medium"
                      : "text-gray-700 hover:bg-white"
                  }`}
                >
                  <span className="flex items-center">
                    <Folder className="w-4 h-4 mr-2" />
                    All Templates
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      selectedFolder === null
                        ? "bg-blue-200 text-blue-800"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {templates.length}
                  </span>
                </button>

                {template_folders.map((folder) => (
                  <div
                    key={folder.id}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-all group cursor-pointer ${
                      selectedFolder === folder.id
                        ? "bg-blue-100 text-blue-700 font-medium"
                        : "text-gray-700 hover:bg-white"
                    }`}
                  >
                    <button
                      onClick={() => setSelectedFolder(folder.id)}
                      className="flex items-center truncate flex-1 text-left cursor-pointer"
                    >
                      <Folder className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{folder.name}</span>
                    </button>
                    <div className="flex items-center gap-1">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                          selectedFolder === folder.id
                            ? "bg-blue-200 text-blue-800"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {folder.templates_count}
                      </span>
                      {is_admin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 hover:bg-gray-200 rounded"
                            >
                              <MoreVertical className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-32">
                            <DropdownMenuItem onClick={() => handleEditFolder(folder)} className="cursor-pointer">
                              <Edit className="w-3 h-3 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteFolder(folder)}
                              className="text-red-600 focus:text-red-600 cursor-pointer"
                            >
                              <Trash2 className="w-3 h-3 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {/* Right Pane - Templates */}
          <div className="col-span-9 flex flex-col overflow-hidden">
            {/* Fixed header + search */}
            <div className="flex-shrink-0 p-6 pb-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedFolder === null
                      ? "All Templates"
                      : template_folders.find(
                          (f) => f.id === selectedFolder
                        )?.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {filteredTemplates.length} template
                    {filteredTemplates.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="relative w-full max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    placeholder="Search templates..."
                    className="w-full h-9 rounded-lg border border-gray-300 bg-white pl-9 pr-8 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {templateSearch && (
                    <button
                      type="button"
                      onClick={() => setTemplateSearch("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 cursor-pointer"
                      title="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable templates */}
            <div className="flex-1 overflow-y-auto p-6 pt-2">
              {filteredTemplates.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[500px] bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="bg-gray-100 rounded-full p-4 mb-4">
                    <FileText className="w-12 h-12 text-gray-400" />
                  </div>
                  <p className="text-lg font-medium text-gray-900">
                    No templates found
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {search
                      ? `No templates match "${templateSearch.trim()}"`
                      : "Upload your first template to get started"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredTemplates.map((template) => {
                    const primaryTag = template.tags?.[0];
                    const tagColor = primaryTag?.color || null;
                    
                    return (
                      <div
                        key={template.id}
                        className={`group relative bg-white rounded-xl hover:ring-2 hover:ring-blue-500 transition-all duration-200 cursor-pointer border-2 ${
                          tagColor ? getCardBorderClass(tagColor) : 'border-gray-200'
                        }`}
                      >
                        <div className="aspect-[4/3] bg-gradient-to-br from-slate-50 to-gray-100 relative overflow-hidden rounded-t-xl">
                        <div className="absolute inset-0 pointer-events-none">
                          {template.html_content ? (
                            <iframe
                              srcDoc={template.html_content}
                              className="absolute inset-0 w-full h-full transform scale-[0.25] origin-top-left pointer-events-none"
                              style={{ width: "400%", height: "400%", overflow: "hidden" }}
                              sandbox="allow-same-origin"
                              scrolling="no"
                            />
                          ) : (
                            // Text-only template: show the plain text itself
                            <pre className="absolute inset-0 overflow-hidden p-3 font-mono text-[6px] leading-snug text-slate-700 whitespace-pre-wrap break-words">
                              {template.text_content}
                            </pre>
                          )}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent pointer-events-none" />
                        
                        {/* Action buttons container */}
                        <div className="absolute inset-x-0 top-0 p-3 flex justify-between">
                          {/* Preview and Edit buttons - visible on hover */}
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onPreviewTemplate(template);
                              }}
                              className="bg-white/95 backdrop-blur-sm rounded-lg px-2.5 h-8 border border-gray-200 hover:bg-white transition-colors cursor-pointer flex items-center"
                              title="Preview template"
                            >
                              <span className="text-xs font-medium text-gray-700">
                                Preview
                              </span>
                            </button>
                            {is_admin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditTemplate(template);
                                }}
                                className="bg-white/95 backdrop-blur-sm rounded-lg px-2.5 h-8 border border-gray-200 hover:bg-white transition-colors cursor-pointer flex items-center"
                                title="Edit template"
                              >
                                <span className="text-xs font-medium text-gray-700">
                                  Edit
                                </span>
                              </button>
                            )}
                          </div>
                          
                          {/* 3-dots menu - always visible */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="bg-white/95 backdrop-blur-sm rounded-lg px-2.5 h-8 border border-gray-200 hover:bg-white transition-colors cursor-pointer flex items-center justify-center"
                                title="More options"
                              >
                                <MoreVertical className="w-3.5 h-3.5 text-gray-700" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem
                                onClick={() => {
                                  handleDownloadTemplate(template);
                                }}
                                className="text-xs cursor-pointer"
                              >
                                <Download className="w-3.5 h-3.5 mr-2" />
                                Download
                              </DropdownMenuItem>
                              {is_admin && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      handleEditName(template);
                                    }}
                                    className="text-xs cursor-pointer"
                                  >
                                    <Edit className="w-3.5 h-3.5 mr-2" />
                                    Edit Name
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      handleManageTag(template);
                                    }}
                                    className="text-xs cursor-pointer"
                                  >
                                    <Tag className="w-3.5 h-3.5 mr-2" />
                                    Manage Tag
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      handleCopyToFolder(template);
                                    }}
                                    className="text-xs cursor-pointer"
                                  >
                                    <Copy className="w-3.5 h-3.5 mr-2" />
                                    Copy to Folder
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      handleMoveToFolder(template);
                                    }}
                                    className="text-xs cursor-pointer"
                                  >
                                    <FolderOpen className="w-3.5 h-3.5 mr-2" />
                                    Move to Folder
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      handleDiscardTemplate(template);
                                    }}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50 text-xs cursor-pointer"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-2" />
                                    Delete Template
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <div className="p-4 bg-white rounded-b-xl">
                        <h4
                          className="font-medium text-slate-900 text-base truncate"
                          title={
                            template.html_content
                              ? `${template.name}.html`
                              : template.name
                          }
                        >
                          {template.name}
                          {template.html_content && ".html"}
                        </h4>
                        {!template.html_content && (
                          <span className="mt-1 inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                            Text only
                          </span>
                        )}
                        {template.tags && template.tags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {template.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${getColorClass(
                                  tag.color
                                )}`}
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Replace Content Confirmation */}
      <Dialog
        open={pendingFile !== null}
        onOpenChange={(open) => !open && setPendingFile(null)}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Replace current content?</DialogTitle>
            <DialogDescription>
              Loading "{pendingFile?.name}" will replace the content currently in
              the editor. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingFile(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmReplace}
              className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer"
            >
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Folder Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Template to Folder</DialogTitle>
            <DialogDescription>
              Select a folder to move "{templateToMove?.name}" to.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="target-folder" className="text-sm font-medium text-gray-700 mb-2">
              Select Folder
            </Label>
            <select
              id="target-folder"
              value={targetFolder || ""}
              onChange={(e) => setTargetFolder(e.target.value ? Number(e.target.value) : null)}
              className="block w-full mt-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="" disabled>Select a folder</option>
              {template_folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmMove}>
              Move Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy to Folder Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy Template to Folder</DialogTitle>
            <DialogDescription>
              Select a different folder to copy "{templateToCopy?.name}" to.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="copy-target-folder" className="text-sm font-medium text-gray-700 mb-2">
              Select Folder
            </Label>
            <select
              id="copy-target-folder"
              value={copyTargetFolder || ""}
              onChange={(e) => setCopyTargetFolder(e.target.value ? Number(e.target.value) : null)}
              className="block w-full mt-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="" disabled>Select a folder</option>
              {template_folders.map((folder) => (
                <option 
                  key={folder.id} 
                  value={folder.id}
                  disabled={folder.id === templateToCopy?.folder_id}
                >
                  {folder.name} {folder.id === templateToCopy?.folder_id && "(current folder)"}
                </option>
              ))}
            </select>
            {copyTargetFolder === templateToCopy?.folder_id && (
              <p className="mt-2 text-sm text-red-600">
                Cannot copy template to the same folder
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmCopy}
              disabled={!copyTargetFolder || copyTargetFolder === templateToCopy?.folder_id}
            >
              Copy Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Tag Dialog */}
      <TemplateTagDialog
        open={tagDialogOpen}
        onOpenChange={setTagDialogOpen}
        template={templateForTag}
        availableTags={tags}
        offerId={offer.id}
      />

      {/* Edit Template Name Dialog */}
      <Dialog open={editNameDialogOpen} onOpenChange={setEditNameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Template Name</DialogTitle>
            <DialogDescription>
              Change the name of "{templateToRename?.name}.html"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="template-name" className="text-sm font-medium text-gray-700 mb-2">
              Template Name
            </Label>
            <div className="flex items-center gap-2 mt-2">
              <Input
                id="template-name"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Enter template name"
                className="flex-1"
              />
              <span className="text-sm text-gray-500">.html</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditNameDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmRename}
              disabled={!newTemplateName.trim() || newTemplateName.trim() === templateToRename?.name}
              className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}